/**
 * Risk Appetite Statement entity adapter (P66.2, #479).
 *
 * Bank-curated statements with 5 SAMA Counter-Fraud-aligned
 * categories. Reuses the P15 admin scaffolding pattern --
 * AdminListView + AdminEntityDrawer drive create / edit / delete
 * via the foundation/admin/risk-appetite-statements endpoints.
 *
 * Disclosure design (mirrors v1):
 *   summary is required (the "meta" the CRO function can put in
 *     writing even when the full board statement is confidential)
 *   4 structured fields optional fuller-disclosure
 *
 * Note: dates land as ISO 8601 strings via text fields at v0.68.0;
 * a datetime widget could land later if operators ask. Backend
 * Pydantic parses the ISO strings server-side.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export type RiskAppetiteCategory =
  | "customer_impact"
  | "organisation_impact"
  | "financial"
  | "regulatory"
  | "operational";

const CATEGORY_LABELS: Record<RiskAppetiteCategory, string> = {
  customer_impact: "Customer impact",
  organisation_impact: "Organisation impact",
  financial: "Financial",
  regulatory: "Regulatory",
  operational: "Operational",
};

export interface RiskAppetiteStatement {
  id: string;
  category: RiskAppetiteCategory;
  statement_title: string;
  summary: string;
  tolerance_qualitative: string | null;
  tolerance_quantitative: string | null;
  escalation_trigger: string | null;
  owner: string | null;
  last_reviewed_at: string;
  next_review_due_at: string | null;
  evidence_link: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface RiskAppetiteStatementIn {
  category: RiskAppetiteCategory;
  statement_title: string;
  summary: string;
  tolerance_qualitative: string | null;
  tolerance_quantitative: string | null;
  escalation_trigger: string | null;
  owner: string | null;
  last_reviewed_at: string;
  next_review_due_at: string | null;
  evidence_link: string | null;
}

export type RiskAppetiteStatementPatch = Partial<RiskAppetiteStatementIn>;

const columns: AdminColumn<RiskAppetiteStatement>[] = [
  {
    key: "category",
    label: "Category",
    sortValue: (r) => r.category,
    render: (r) => (
      <span
        data-testid={`ras-category-${r.category}`}
        className="inline-flex items-center rounded-full bg-indigo-100 text-indigo-900 px-2 py-0.5 text-xs font-medium"
      >
        {CATEGORY_LABELS[r.category]}
      </span>
    ),
  },
  {
    key: "title",
    label: "Title",
    sortValue: (r) => r.statement_title,
    render: (r) => (
      <span className="font-medium text-slate-900">{r.statement_title}</span>
    ),
  },
  {
    key: "summary",
    label: "Summary",
    render: (r) => (
      <span className="text-xs text-slate-600 line-clamp-2">{r.summary}</span>
    ),
  },
  {
    key: "owner",
    label: "Owner",
    render: (r) =>
      r.owner ? (
        <span className="text-xs text-slate-700">{r.owner}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "last_reviewed_at",
    label: "Last reviewed",
    sortValue: (r) => r.last_reviewed_at,
    render: (r) => (
      <span className="text-xs text-slate-600">{r.last_reviewed_at.slice(0, 10)}</span>
    ),
  },
];

const formFields: FormField<RiskAppetiteStatementIn>[] = [
  {
    kind: "select",
    key: "category",
    label: "Category",
    required: true,
    options: (Object.keys(CATEGORY_LABELS) as RiskAppetiteCategory[]).map((v) => ({
      value: v,
      label: CATEGORY_LABELS[v],
    })),
  },
  {
    kind: "text",
    key: "statement_title",
    label: "Statement title",
    required: true,
    maxLength: 255,
  },
  {
    kind: "textarea",
    key: "summary",
    label: "Summary (required, regulator-facing disclosure)",
    rows: 4,
  },
  {
    kind: "textarea",
    key: "tolerance_qualitative",
    label: "Qualitative tolerance (optional)",
    rows: 3,
  },
  {
    kind: "textarea",
    key: "tolerance_quantitative",
    label: "Quantitative tolerance (optional)",
    rows: 3,
  },
  {
    kind: "textarea",
    key: "escalation_trigger",
    label: "Escalation trigger (optional)",
    rows: 2,
  },
  { kind: "text", key: "owner", label: "Owner (optional)", maxLength: 128 },
  {
    kind: "datetime",
    key: "last_reviewed_at",
    label: "Last reviewed at",
    required: true,
  },
  {
    kind: "datetime",
    key: "next_review_due_at",
    label: "Next review due (optional)",
  },
  { kind: "text", key: "evidence_link", label: "Evidence link (optional)" },
];

export const riskAppetiteAdapter: EntityAdapter<
  RiskAppetiteStatement,
  RiskAppetiteStatementIn,
  RiskAppetiteStatementPatch
> = {
  i18nPrefix: "modules.foundation.admin.riskAppetite",
  /*
   * Round 10 C3 -- the journey spine badges this step, attributes it to
   * "CRO / business owner", offers her a "Go ->", and landed her on a
   * page that refused her. A risk appetite is the CRO's own artefact:
   * the one register on this shelf that is unambiguously hers. The
   * server gate moved with it; this is the affordance, not the gate.
   */
  canWrite: (me) =>
    (me?.is_admin ?? false) ||
    (me?.realm_roles ?? []).some((r) => r === "cro" || r === "business_owner"),
  // Round 19 Q6 -- "New statement" offered to the service owner said
  // nothing of whose register it is. A business owner may add one; the
  // register is the CRO's.
  createNoteKey: (me) =>
    me?.is_admin || (me?.realm_roles ?? []).includes("cro")
      ? null
      : "modules.foundation.admin.riskAppetite.list.ownerNote",
  endpoint: "/v1/admin/foundation/risk-appetite-statements",
  queryKey: ["foundation-admin", "risk-appetite-statements"] as const,
  columns,
  defaultSort: { key: "category", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.category} ${r.statement_title} ${r.summary} ${r.owner ?? ""}`,
  formFields,
  formInitial: () => ({
    category: "customer_impact",
    statement_title: "",
    summary: "",
    tolerance_qualitative: null,
    tolerance_quantitative: null,
    escalation_trigger: null,
    owner: null,
    last_reviewed_at: "",
    next_review_due_at: null,
    evidence_link: null,
  }),
  rowToPatch: (r) => ({
    category: r.category,
    statement_title: r.statement_title,
    summary: r.summary,
    tolerance_qualitative: r.tolerance_qualitative,
    tolerance_quantitative: r.tolerance_quantitative,
    escalation_trigger: r.escalation_trigger,
    owner: r.owner,
    last_reviewed_at: r.last_reviewed_at,
    next_review_due_at: r.next_review_due_at,
    evidence_link: r.evidence_link,
  }),
};
